// Lazy-loaded Three.js viewer. Constructed only when a model
// block enters the viewport — keeps initial bundle small for
// projects that have only photos/video.

import { getLenis } from './scroll.js';

let THREE_NS = null;
let GLTFLoaderClass = null;
let OrbitControlsClass = null;
let DRACOLoaderClass = null;
let sharedDracoLoader = null;

// Material presets applied via `block.material` on a model block.
// Each entry is a MeshStandardMaterial constructor option object.
// Colours are sRGB hex; PBR values map directly to glTF semantics.
const MATERIAL_PRESETS = {
  silver: { color: 0xe6e6e6, metalness: 1.0, roughness: 0.1 },
  gold:   { color: 0xffc46b, metalness: 1.0, roughness: 0.15 },
  copper: { color: 0xc97e5f, metalness: 1.0, roughness: 0.2 },
  // Brass with a polished/waxed finish. Saturated yellow (close to
  // the canonical brass hex #b5a642) and low roughness so it reads
  // as obviously metallic next to the matte beech wood — without
  // the boost it tonally blends with the wood preset.
  brass:  { color: 0xd6b332, metalness: 1.0, roughness: 0.18 },
  matte:  { color: 0xb0b0b0, metalness: 0.0, roughness: 0.85 },
};

// Procedural wood textures. Built once on demand, then reused by
// every part that requests the same `wood` preset — beech and oak
// share a single CanvasTexture across the page.
const WOOD_PRESETS = {
  beech: {
    base: '#e6cb9e',
    stripe: '#d4b07e',
    streak: '#8a6539',
    streakCount: 10,
    fineNoise: 10,
  },
  oak: {
    base: '#d2ad7a',
    stripe: '#b78a52',
    streak: '#6b4a23',
    streakCount: 18,
    fineNoise: 14,
  },
};
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}
const woodTextureCache = new Map();
function ensureWoodTexture(name) {
  if (woodTextureCache.has(name)) return woodTextureCache.get(name);
  const preset = WOOD_PRESETS[name] || WOOD_PRESETS.beech;
  const [sr, sg, sb] = hexToRgb(preset.stripe);
  const [kr, kg, kb] = hexToRgb(preset.streak);
  const W = 1024, H = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = preset.base;
  ctx.fillRect(0, 0, W, H);
  // Vertical grain: each column gets a slightly different tint,
  // varied by a low-frequency sine + a touch of jitter so adjacent
  // columns look related but not identical. Result reads as straight
  // long grain running along the cylinder's length.
  for (let x = 0; x < W; x++) {
    const wave = Math.sin(x * 0.018) * 0.5 + Math.sin(x * 0.07) * 0.25;
    const jitter = (Math.random() - 0.5) * 0.4;
    const alpha = Math.max(0, Math.min(0.55, 0.25 + wave * 0.18 + jitter * 0.1));
    ctx.fillStyle = `rgba(${sr}, ${sg}, ${sb}, ${alpha})`;
    ctx.fillRect(x, 0, 1, H);
  }
  // Occasional darker streaks for growth-ring character.
  for (let i = 0; i < preset.streakCount; i++) {
    const x = Math.random() * W;
    const width = 1 + Math.random() * 3;
    const a = 0.06 + Math.random() * 0.18;
    ctx.fillStyle = `rgba(${kr}, ${kg}, ${kb}, ${a})`;
    ctx.fillRect(x, 0, width, H);
  }
  // Fine surface noise so the wood doesn't read as plastic-smooth.
  const img = ctx.getImageData(0, 0, W, H);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * preset.fineNoise;
    d[i]     = Math.max(0, Math.min(255, d[i]     + n));
    d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + n));
    d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + n));
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE_NS.CanvasTexture(canvas);
  tex.colorSpace = THREE_NS.SRGBColorSpace;
  tex.wrapS = THREE_NS.RepeatWrapping;
  tex.wrapT = THREE_NS.RepeatWrapping;
  tex.anisotropy = 8;
  woodTextureCache.set(name, tex);
  return tex;
}

// Cylindrical UV projection around `axis` (default Y). U wraps the
// azimuth, V runs along the axis. Used to lay wood grain along the
// length of a turned/extruded part — the GLBs from CAD typically
// ship without useful UVs, so we synthesize them.
function applyCylindricalUVs(geometry, axis = 'y', tilesAround = 1, tilesAlong = 1) {
  geometry.computeBoundingBox();
  const bb = geometry.boundingBox;
  const axisIdx = { x: 0, y: 1, z: 2 }[axis] ?? 1;
  const u1 = (axisIdx + 1) % 3;
  const u2 = (axisIdx + 2) % 3;
  const minA = bb.min.getComponent(axisIdx);
  const maxA = bb.max.getComponent(axisIdx);
  const rangeA = Math.max(1e-6, maxA - minA);
  const cx = (bb.min.getComponent(u1) + bb.max.getComponent(u1)) / 2;
  const cz = (bb.min.getComponent(u2) + bb.max.getComponent(u2)) / 2;
  const pos = geometry.attributes.position;
  const arr = new Float32Array(pos.count * 2);
  const tmp = [0, 0, 0];
  for (let i = 0; i < pos.count; i++) {
    tmp[0] = pos.getX(i); tmp[1] = pos.getY(i); tmp[2] = pos.getZ(i);
    const theta = Math.atan2(tmp[u2] - cz, tmp[u1] - cx);
    arr[i * 2]     = (theta / (2 * Math.PI) + 0.5) * tilesAround;
    arr[i * 2 + 1] = ((tmp[axisIdx] - minA) / rangeA) * tilesAlong;
  }
  geometry.setAttribute('uv', new THREE_NS.BufferAttribute(arr, 2));
}

// Planar UV projection onto the plane perpendicular to `axis`
// (default Y — a straight-down "from above" projection), used for
// photographic slab/stone textures where the material should read
// as one continuous surface rather than wrap around the form. U/V
// come from the two non-axis world coordinates, normalized to the
// mesh's own bbox into a plain 0..1 range — scale/rotation/offset
// are NOT baked here; they're set as live properties on the part's
// cloned Texture (repeat/rotation/offset/center) so a tuning UI can
// adjust them with a cheap property set instead of a geometry rebake.
function applyPlanarUVs(geometry, axis = 'y') {
  geometry.computeBoundingBox();
  const bb = geometry.boundingBox;
  const axisIdx = { x: 0, y: 1, z: 2 }[axis] ?? 1;
  const u1 = (axisIdx + 1) % 3;
  const u2 = (axisIdx + 2) % 3;
  const min1 = bb.min.getComponent(u1), max1 = bb.max.getComponent(u1);
  const min2 = bb.min.getComponent(u2), max2 = bb.max.getComponent(u2);
  const range1 = Math.max(1e-6, max1 - min1);
  const range2 = Math.max(1e-6, max2 - min2);
  const pos = geometry.attributes.position;
  const arr = new Float32Array(pos.count * 2);
  const tmp = [0, 0, 0];
  for (let i = 0; i < pos.count; i++) {
    tmp[0] = pos.getX(i); tmp[1] = pos.getY(i); tmp[2] = pos.getZ(i);
    arr[i * 2] = (tmp[u1] - min1) / range1;
    arr[i * 2 + 1] = (tmp[u2] - min2) / range2;
  }
  geometry.setAttribute('uv', new THREE_NS.BufferAttribute(arr, 2));
}

// Image textures for `part.texture` (a photographic material map, as
// opposed to the procedural `wood` presets above). Cached by src so
// multiple parts referencing the same file share one Texture/upload.
const imageTextureCache = new Map();
function ensureImageTexture(src) {
  if (imageTextureCache.has(src)) return imageTextureCache.get(src);
  const tex = new THREE_NS.TextureLoader().load(src);
  tex.colorSpace = THREE_NS.SRGBColorSpace;
  tex.wrapS = THREE_NS.RepeatWrapping;
  tex.wrapT = THREE_NS.RepeatWrapping;
  tex.anisotropy = 8;
  imageTextureCache.set(src, tex);
  return tex;
}

// A minimal studio environment for PBR reflections — bright ceiling
// and side panels, dim floor. Used in place of Three.js's stock
// `RoomEnvironment` so reflective materials don't pick up a bright
// hotspot from below (which made the silver flower's underside read
// as a lit floor reflection).
function buildStudioEnvScene(THREE) {
  const scene = new THREE.Scene();
  const geom = new THREE.BoxGeometry();
  geom.deleteAttribute('uv');

  // Surrounding box — neutral grey walls. Brighter than charcoal so
  // the silver picks up an ambient fill rather than reading pitch
  // black in its shadowed regions.
  const wallMat = new THREE.MeshStandardMaterial({
    color: 0x808080,
    roughness: 1,
    side: THREE.BackSide,
  });
  const room = new THREE.Mesh(geom, wallMat);
  room.scale.set(40, 40, 40);
  scene.add(room);

  const light = (intensity) => {
    const m = new THREE.MeshBasicMaterial({ color: 0xffffff });
    m.color.multiplyScalar(intensity);
    return new THREE.Mesh(geom, m);
  };

  // Bright ceiling — primary key direction (top reflections).
  const ceiling = light(5);
  ceiling.position.set(0, 18, 0);
  ceiling.scale.set(14, 0.5, 14);
  scene.add(ceiling);

  // Side fills — left & right at mid-height.
  for (const x of [-18, 18]) {
    const side = light(2.5);
    side.position.set(x, 4, 0);
    side.scale.set(0.5, 12, 16);
    scene.add(side);
  }
  // Front & back fills.
  for (const z of [-18, 18]) {
    const fb = light(1.4);
    fb.position.set(0, 4, z);
    fb.scale.set(16, 12, 0.5);
    scene.add(fb);
  }

  // Floor bounce — bright enough that the underside reads, but
  // well below the ceiling so the model doesn't look lit from below.
  const floor = light(2.5);
  floor.position.set(0, -16, 0);
  floor.scale.set(14, 0.5, 14);
  scene.add(floor);

  return scene;
}

let RGBELoaderClass = null;
let BufferGeometryUtilsNS = null;

async function ensureThree() {
  if (THREE_NS) return;
  const [three, gltf, orbit, draco, rgbe, bgu] = await Promise.all([
    import('three'),
    import('three/addons/loaders/GLTFLoader.js'),
    import('three/addons/controls/OrbitControls.js'),
    import('three/addons/loaders/DRACOLoader.js'),
    import('three/addons/loaders/RGBELoader.js'),
    import('three/addons/utils/BufferGeometryUtils.js'),
  ]);
  THREE_NS = three;
  GLTFLoaderClass = gltf.GLTFLoader;
  OrbitControlsClass = orbit.OrbitControls;
  DRACOLoaderClass = draco.DRACOLoader;
  RGBELoaderClass = rgbe.RGBELoader;
  BufferGeometryUtilsNS = bgu;
}

// One DRACOLoader instance shared by all viewers — the decoder is
// fetched once from the official three.js CDN. Swap to a self-hosted
// path under /public/draco/ if you want zero third-party requests.
function getDracoLoader() {
  if (sharedDracoLoader) return sharedDracoLoader;
  sharedDracoLoader = new DRACOLoaderClass();
  sharedDracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
  // WASM in workers — keeps Draco decoding off the main thread.
  // (Previously `{ type: 'js' }` ran the decoder on the UI thread,
  // freezing the page during heavy loads.)
  sharedDracoLoader.setDecoderConfig({ type: 'wasm' });
  sharedDracoLoader.setWorkerLimit(4);
  return sharedDracoLoader;
}

/**
 * Create a viewer in `container`.
 * Options on the model block manifest:
 *   - src           path to .glb/.gltf (relative to project folder)
 *   - builtin       'torusKnot' | 'sphere' (when no GLB yet)
 *   - background    hex string for clear color
 *   - autorotate    boolean
 *   - rotateSpeed   number (radians/sec) when autorotate
 *   - scrubScroll   tie rotation to page scroll progress (0..1)
 *   - hint          short label shown bottom-left
 */
export async function mountModelViewer(container, block, getScrollProgress) {
  await ensureThree();
  const THREE = THREE_NS;

  const scene = new THREE.Scene();
  if (!block.transparent) {
    scene.background = new THREE.Color(block.background || '#000000');
  }

  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 1000);
  camera.position.set(0, 0.6, 4);

  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: !!block.transparent,
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  if (block.transparent) renderer.setClearColor(0x000000, 0);
  container.appendChild(renderer.domElement);

  // Lighting
  const hemi = new THREE.HemisphereLight(0xffffff, 0x222244, 0.6);
  scene.add(hemi);
  const key = new THREE.DirectionalLight(0xffffff, 1.6);
  key.position.set(3, 4, 5);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x99aaff, 0.8);
  rim.position.set(-4, 1, -3);
  scene.add(rim);

  const root = new THREE.Group();
  scene.add(root);

  // Controls
  const controls = new OrbitControlsClass(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.enableZoom = block.enableZoom !== false; // opt-out via manifest
  // Lock vertical orbit (polar angle) so up/down drags don't tilt
  // the camera.
  if (block.lockVerticalOrbit) {
    controls.minPolarAngle = Math.PI / 2;
    controls.maxPolarAngle = Math.PI / 2;
  }
  // On touch devices (phones, tablets) we dispose OrbitControls
  // entirely so its event listeners (pointerdown / move / up) are
  // unbound from the canvas. `enabled = false` isn't enough — the
  // listeners stay attached and the browser still waits on them
  // before deciding whether a gesture is a page scroll or not.
  // After dispose, the canvas has zero JS handlers; combined with
  // `touch-action: pan-y` in the CSS, vertical drags become plain
  // page scrolls. Desktop mouse / trackpad are unaffected because
  // `maxTouchPoints` is 0 on those.
  const isTouch = typeof window !== 'undefined'
    && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);
  if (block.touchScrollPassthrough && isTouch) {
    controls.dispose();
  }
  controls.minDistance = 1.5;
  controls.maxDistance = 8;
  controls.enabled = !block.scrubScroll; // scroll-driven projects shouldn't fight the user

  // Optional reflective-material override. When `block.material` is
  // set (e.g. 'silver'), every mesh in the loaded GLB gets its
  // material swapped for a MeshStandardMaterial with the matching
  // metalness/roughness/colour, and the scene is lit by a PMREM-
  // filtered RoomEnvironment so the reflections have something to
  // reflect.
  let materialPreset = MATERIAL_PRESETS[block.material];
  // Custom solid colour without a named preset — e.g. `color: "#e0200f"`.
  // Renders as a matte painted finish; the environment still lights it
  // so the form reads volumetrically rather than flat.
  if (!materialPreset && block.color) {
    materialPreset = {
      color: new THREE.Color(block.color).getHex(),
      metalness: block.metalness ?? 0,
      roughness: block.roughness ?? 0.55,
    };
  }
  // Build the studio PMREM env whenever the block OR any of its
  // parts use a metallic preset — without an env, metallic surfaces
  // render nearly black since they have no diffuse component.
  // `env: true` forces it for GLBs that keep their own (metallic)
  // materials, e.g. KeyShot exports.
  const partsNeedEnv = Array.isArray(block.parts) && block.parts.some(
    (p) => p && (MATERIAL_PRESETS[p.material] || p.color || p.wood || p.texture)
  );
  let envRT = null;
  if (materialPreset || partsNeedEnv || block.env) {
    const pmrem = new THREE.PMREMGenerator(renderer);
    pmrem.compileEquirectangularShader();
    if (block.envHdr) {
      // Equirectangular .hdr (Radiance RGBE) → PMREM. Used for
      // photographic studio lookups; the underside of the model
      // picks up actual floor light rather than the procedural
      // box environment's approximations.
      try {
        const loader = new RGBELoaderClass();
        const tex = await loader.loadAsync(block.envHdr);
        envRT = pmrem.fromEquirectangular(tex);
        tex.dispose();
      } catch (err) {
        console.warn('[three] HDR load failed, falling back to procedural env', err);
        envRT = pmrem.fromScene(buildStudioEnvScene(THREE), 0.04);
      }
    } else {
      envRT = pmrem.fromScene(buildStudioEnvScene(THREE), 0.04);
    }
    scene.environment = envRT.texture;
    if (block.envIntensity != null && 'environmentIntensity' in scene) {
      scene.environmentIntensity = block.envIntensity;
    }
    pmrem.dispose();
  }

  // Content. Supports either a single `src` or a `parts` array of
  // {src, color, metalness, roughness} — every part shares the
  // camera and lighting but can have its own material. Used for
  // multi-piece objects (e.g. Circle Lurks: nose + surrounding air).
  let mixer = null;
  // Bbox size of the loaded composite, cached so resize can re-fit
  // the camera without recentering the model. Stays null until a
  // GLB-based block finishes loading.
  let fitSize = null;
  const fitPadding = block.cameraPadding ?? 1.4;
  const fitElevation = block.cameraElevation ?? 0.15;
  // Subtle in/out camera pulse during autorotate — helps non-square
  // bbox models (a tall narrow shelf) breathe instead of feeling
  // statically framed. 0 disables. Period is in seconds.
  const breatheAmp = block.breathe ?? 0;
  const breathePeriod = block.breathePeriod ?? 6;
  const parts = Array.isArray(block.parts) && block.parts.length
    ? block.parts
    : (block.src ? [{ src: block.src }] : []);
  if (parts.length) {
    const loader = new GLTFLoaderClass();
    loader.setDRACOLoader(getDracoLoader());
    const composite = new THREE.Group();
    // Track the part with `pivot: true` so rotation happens around
    // its bbox center rather than the combined-parts centroid.
    // Without this, a heavy main body surrounded by smaller outliers
    // visibly wobbles because the centroid sits between them.
    let pivotObj = null;
    for (const part of parts) {
      try {
        const gltf = await loader.loadAsync(part.src);
        const m = gltf.scene;
        // Per-part material: explicit color overrides the block-level
        // preset; falls back to the block's preset if no per-part
        // color is set.
        let partPreset = MATERIAL_PRESETS[part.material] || materialPreset;
        if (part.color) {
          partPreset = {
            color: new THREE.Color(part.color).getHex(),
            metalness: part.metalness ?? 0,
            roughness: part.roughness ?? 0.55,
          };
        } else if (partPreset && (part.metalness != null || part.roughness != null)) {
          partPreset = { ...partPreset };
          if (part.metalness != null) partPreset.metalness = part.metalness;
          if (part.roughness != null) partPreset.roughness = part.roughness;
        }
        // Wood path: a procedural grain texture mapped cylindrically
        // along `woodAxis` (default Y — vertical legs). Wins over
        // colour/material on the same part. `woodTilesAround` /
        // `woodTilesAlong` repeat the texture so the grain density
        // can be tuned per-model. `woodMinAxisExtent` is a per-mesh
        // filter: meshes whose extent along `woodAxis` is shorter
        // than this fall back to the part's material/color instead
        // of getting the wood map — lets a single file mix wood
        // pillars with brass caps without re-exporting.
        const woodMap = part.wood ? ensureWoodTexture(part.wood) : null;
        const woodAxisName = part.woodAxis || 'y';
        const woodMinExtent = part.woodMinAxisExtent ?? 0;
        // Photographic texture path (`part.texture`): a real image
        // (stone, marble, etc.) rather than the procedural wood grain.
        // Wins over `wood` and colour/material on the same part.
        // `textureMapping` picks the UV projection — 'planar' (default)
        // is a straight-down projection onto the plane perpendicular
        // to `textureAxis` (from above, for axis 'y'); 'cylindrical'
        // wraps around the axis like the wood path. `textureRotation`
        // spins the projected UVs in-plane (degrees) so the same
        // source image can be reused with a different grain direction
        // per part without re-editing the file.
        // Cloned per-part (not just cached per-src) so two parts
        // sharing one image file can carry independent repeat/offset/
        // rotation, set as Texture-matrix properties rather than
        // baked into the UVs.
        const imageMap = part.texture ? ensureImageTexture(part.texture).clone() : null;
        if (imageMap) {
          imageMap.needsUpdate = true;
          imageMap.repeat.set(part.textureTilesU ?? 1, part.textureTilesV ?? 1);
          imageMap.offset.set(part.textureOffsetU ?? 0, part.textureOffsetV ?? 0);
          imageMap.center.set(0.5, 0.5);
          imageMap.rotation = ((part.textureRotation ?? 0) * Math.PI) / 180;
        }
        const textureAxisName = part.textureAxis || 'y';
        const textureMapping = part.textureMapping || 'planar';
        const fallbackPreset = partPreset ? (() => {
          const p = { ...partPreset };
          if (block.envTint) {
            const base = new THREE.Color(p.color);
            const tint = new THREE.Color(block.envTint);
            p.color = base.multiply(tint).getHex();
          }
          return p;
        })() : null;
        const woodFinal = woodMap ? {
          map: woodMap,
          metalness: part.metalness ?? 0,
          roughness: part.roughness ?? 0.7,
        } : null;
        const imageFinal = imageMap ? {
          map: imageMap,
          metalness: part.metalness ?? 0,
          roughness: part.roughness ?? 0.7,
        } : null;
        const needsOverride = !!(imageFinal || woodFinal || fallbackPreset);
        if (needsOverride) {
          const smooth = (part.smoothShade ?? block.smoothShade) !== false;
          const tol = part.smoothTolerance ?? block.smoothTolerance ?? 1e-3;
          const axisIdx = { x: 0, y: 1, z: 2 }[woodAxisName] ?? 1;
          m.traverse((o) => {
            if (!o.isMesh) return;
            if (smooth) {
              if (!o.geometry.index) {
                o.geometry = BufferGeometryUtilsNS.mergeVertices(o.geometry, tol);
              }
              o.geometry.deleteAttribute('normal');
              o.geometry.computeVertexNormals();
            }
            let useWood = !imageFinal && !!woodFinal;
            if (useWood && woodMinExtent > 0) {
              o.geometry.computeBoundingBox();
              const bb = o.geometry.boundingBox;
              const ext = bb.max.getComponent(axisIdx) - bb.min.getComponent(axisIdx);
              if (ext < woodMinExtent) useWood = false;
            }
            let preset;
            if (imageFinal) {
              if (textureMapping === 'cylindrical') {
                applyCylindricalUVs(
                  o.geometry,
                  textureAxisName,
                  part.textureTilesAround ?? 1,
                  part.textureTilesAlong ?? 1
                );
              } else {
                // Identity 0..1 mapping — scale/rotation/offset live
                // on the cloned Texture's repeat/rotation/offset
                // (set above) instead of being baked into the UVs.
                applyPlanarUVs(o.geometry, textureAxisName);
              }
              preset = imageFinal;
            } else if (useWood) {
              applyCylindricalUVs(
                o.geometry,
                woodAxisName,
                part.woodTilesAround ?? 1,
                part.woodTilesAlong ?? 1
              );
              preset = woodFinal;
            } else if (fallbackPreset) {
              preset = fallbackPreset;
            } else {
              o.castShadow = false;
              o.receiveShadow = false;
              return;
            }
            o.material = new THREE.MeshStandardMaterial(preset);
            o.castShadow = false;
            o.receiveShadow = false;
          });
        }
        // Disable frustum culling on every mesh of this part so
        // a wide Y-axis spin can't drop a mesh whose bounding
        // sphere briefly leaves the camera frustum. The static
        // bbox-based camera fit is otherwise tight enough to make
        // this happen at rotation extremes.
        //
        // Also force bbox/sphere recomputation: some GLBs ship with
        // corrupt accessor min/max (e.g. min > max), and GLTFLoader
        // uses those values to set geometry.boundingBox. A wrong bbox
        // then propagates into the camera-fit, framing the model in
        // a tiny corner of the canvas.
        m.traverse((o) => {
          if (!o.isMesh) return;
          o.frustumCulled = false;
          if (o.geometry) {
            o.geometry.computeBoundingBox();
            o.geometry.computeBoundingSphere();
          }
        });
        composite.add(m);
        if (part.pivot && !pivotObj) pivotObj = m;
        if (gltf.animations?.length) {
          mixer = mixer || new THREE.AnimationMixer(composite);
          gltf.animations.forEach((clip) => mixer.clipAction(clip).play());
        }
      } catch (err) {
        console.warn('[three] failed to load part', part.src, err);
      }
    }
    // Optional fixed orientation, applied before fitToView so the
    // bounding box matches what's on screen. Values are in degrees.
    if (block.rotateX) composite.rotation.x = block.rotateX * Math.PI / 180;
    if (block.rotateY) composite.rotation.y = block.rotateY * Math.PI / 180;
    if (block.rotateZ) composite.rotation.z = block.rotateZ * Math.PI / 180;
    // Optional mesh filter (e.g. drop scale-ref geometry before the
    // bbox is computed for the camera fit). The callback receives any
    // drawable (Mesh, Line, LineSegments, Points) and returns false to
    // remove it. Hidden objects are detached from the scene graph so
    // they don't contribute to the world bbox.
    if (typeof block.meshFilter === 'function') {
      const toRemove = [];
      composite.traverse((o) => {
        if ((o.isMesh || o.isLine || o.isLineSegments || o.isPoints) && !block.meshFilter(o)) {
          toRemove.push(o);
        }
      });
      for (const o of toRemove) o.parent?.remove(o);
    }
    fitSize = fitToView(composite, camera, fitPadding, controls, pivotObj, fitElevation);
    root.add(composite);
  } else if (block.builtin === 'sphere') {
    root.add(buildSphere(THREE));
  } else {
    root.add(buildTorusKnot(THREE));
  }

  if (block.hint) {
    const tag = document.createElement('div');
    tag.className = 'hint';
    tag.textContent = block.hint;
    container.appendChild(tag);
  }

  // Resize handling. Updates the projection aspect AND re-fits the
  // camera distance — without the re-fit, a model framed at aspect 1
  // would clip on the sides once the container settles to its real
  // (often wider or narrower) aspect.
  const ro = new ResizeObserver(() => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    if (fitSize) applyCameraFit(camera, fitSize, fitPadding, controls, fitElevation);
    else camera.updateProjectionMatrix();
  });
  ro.observe(container);

  // Render only when on-screen
  let inView = true;
  const visIO = new IntersectionObserver(
    (entries) => entries.forEach((e) => (inView = e.isIntersecting)),
    { threshold: 0.01 }
  );
  visIO.observe(container);

  // Animate
  const clock = new THREE.Clock();
  let raf = 0;
  const rotateSpeed = block.rotateSpeed ?? 0.4;
  // Last applied breathe factor, used to remove the previous frame's
  // distance scaling before applying the current frame's.
  let lastBreatheFactor = 1;

  function tick() {
    raf = requestAnimationFrame(tick);
    if (!inView) return;
    const dt = clock.getDelta();

    if (block.scrubScroll && typeof getScrollProgress === 'function') {
      // Map page progress through the model section to a rotation.
      const t = getScrollProgress(container);
      root.rotation.y = t * Math.PI * 2;
      root.rotation.x = Math.sin(t * Math.PI) * 0.25;
    } else if (block.wobble) {
      // Circular wobble — model rotation traces a circle: left →
      // up → right → down → left. Y handles the left/right yaw, X
      // handles the up/down pitch. 90° out of phase so the motion
      // is continuous and never stops at an axis-aligned pose.
      const amp = block.wobbleAmplitude ?? 0.35;
      const speed = block.rotateSpeed ?? 0.5;
      const t = clock.elapsedTime * speed;
      root.rotation.y = -Math.cos(t) * amp;
      root.rotation.x = Math.sin(t) * amp;
    } else if (block.autorotate ?? true) {
      root.rotation.y += rotateSpeed * dt;
    }

    if (mixer) mixer.update(dt);
    controls.update();
    // Breathe — gentle in/out pulse on top of OrbitControls' tracked
    // camera distance. Each frame we undo last frame's factor before
    // applying this frame's, so the offset doesn't compound and drift.
    if (breatheAmp > 0) {
      const f = 1 + breatheAmp * Math.sin(clock.elapsedTime * (2 * Math.PI / breathePeriod));
      const offset = camera.position.clone().sub(controls.target);
      const cur = offset.length();
      if (cur > 1e-6) {
        const baseDist = cur / lastBreatheFactor;
        offset.setLength(baseDist * f);
        camera.position.copy(controls.target).add(offset);
        lastBreatheFactor = f;
      }
    }
    renderer.render(scene, camera);
  }
  tick();

  return {
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      visIO.disconnect();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}

/**
 * Mount a sequenced line-rendered viewer.
 *
 * Block options:
 *   - folder        path to a folder containing .glb files + index.json
 *                   (the index lists files in display order)
 *   - background    hex string for clear color
 *   - lineColor     hex string for the line color
 *   - edgeAngle     EdgesGeometry threshold in degrees (default 30)
 *   - duration      seconds for full reveal animation (default 6)
 *   - scrubScroll   if true, progress is driven by section scroll
 *   - hint          short label shown bottom-left
 *
 * The animation reveals models in folder/index order. Within each
 * model, child meshes are sorted by world-space center along the
 * dominant axis (left→right if X-spread > Y-spread, else bottom→top)
 * and revealed in that sorted order.
 */
export async function mountModelSequence(container, block, getScrollProgress) {
  await ensureThree();
  const THREE = THREE_NS;

  // Fast path: pre-baked binary line file. Skips Draco decoding,
  // EdgesGeometry computation, and slice math entirely — just upload
  // two typed arrays + an index buffer to the GPU.
  if (block.bakedFile) {
    // On phones / narrow viewports / declared slow connections, prefer
    // a coarser mobile bake when the manifest provides one.
    const mobileBin = block.bakedFileMobile;
    const isMobile =
      typeof window !== 'undefined' &&
      (window.matchMedia?.('(max-width: 900px)')?.matches ||
        ['slow-2g', '2g', '3g'].includes(navigator.connection?.effectiveType));
    const url = mobileBin && isMobile ? mobileBin : block.bakedFile;
    return mountBakedLines(container, block, url, getScrollProgress);
  }

  const indexUrl = `${block.folder}/index.json`;
  let index;
  try {
    const r = await fetch(indexUrl);
    if (!r.ok) throw new Error(`${indexUrl}: ${r.status}`);
    index = await r.json();
  } catch (err) {
    console.error('[three] failed to read sequence index', err);
    return null;
  }

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(block.background || '#000000');

  const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100000);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.appendChild(renderer.domElement);

  const root = new THREE.Group();
  scene.add(root);

  const lineColor = new THREE.Color(block.lineColor || '#ffffff');
  const sharedMaterial = makeLineMaterial(THREE, lineColor, block.lineOpacity ?? 0.7);

  const N = index.length;
  const edgeAngle = block.edgeAngle ?? 20;
  const flowRe = block.flowPattern ? new RegExp(block.flowPattern, 'i') : null;
  const flowExcludeRe = block.flowExclude ? new RegExp(block.flowExclude, 'i') : null;
  const flowSlices = block.flowSlices ?? 14;

  const status = document.createElement('div');
  status.className = 'hint';
  status.textContent = `Loading 0 / ${N}`;
  container.appendChild(status);

  const loader = new GLTFLoaderClass();
  loader.setDRACOLoader(getDracoLoader());

  // Streaming pipeline: load all in parallel (Draco workers), but
  // process them sequentially and yield to the browser between each
  // model so the page never freezes for more than one model's work.
  const loadPromises = index.map((file) =>
    loader.loadAsync(`${block.folder}/${file}`).catch((err) => {
      console.warn('[three] failed to load', file, err);
      return null;
    })
  );

  let loadedCount = 0;
  for (let i = 0; i < loadPromises.length; i++) {
    loadPromises[i].then(() => {
      loadedCount++;
      status.textContent = `Loading ${loadedCount} / ${N}`;
    });
  }

  for (let modelIdx = 0; modelIdx < N; modelIdx++) {
    status.textContent = `Building ${modelIdx + 1} / ${N}`;
    const gltf = await loadPromises[modelIdx];
    if (!gltf) continue;

    const filename = index[modelIdx];
    const isFlow =
      flowRe && flowRe.test(filename) && !(flowExcludeRe && flowExcludeRe.test(filename));

    gltf.scene.updateMatrixWorld(true);

    // Bake every mesh's world matrix into a fresh geometry — lets us
    // compute world-space centers and lay out without nested groups.
    const parts = [];
    gltf.scene.traverse((obj) => {
      if (obj.isMesh && obj.geometry) {
        const g = obj.geometry.clone();
        g.applyMatrix4(obj.matrixWorld);
        g.computeBoundingBox();
        const center = g.boundingBox.getCenter(new THREE.Vector3());
        parts.push({ geometry: g, center });
      }
    });
    if (parts.length === 0) {
      console.warn(`[sequence] ${filename}: no meshes`);
      continue;
    }

    if (block.debug) {
      const bb = new THREE.Box3();
      for (const p of parts) bb.union(p.geometry.boundingBox);
      const sz = bb.getSize(new THREE.Vector3());
      console.log(
        `[sequence] ${filename}  parts=${parts.length}  flow=${isFlow}  ` +
          `bbox=(${sz.x.toFixed(2)},${sz.y.toFixed(2)},${sz.z.toFixed(2)})  ` +
          `min=(${bb.min.x.toFixed(2)},${bb.min.y.toFixed(2)},${bb.min.z.toFixed(2)})`
      );
    }

    const slotStart = modelIdx / N;
    const slotEnd = (modelIdx + 1) / N;

    if (isFlow) {
      // Flow rendering: slice the assembled part along its longest
      // bbox axis. Each slice contour reveals at its own moment, so
      // lines visually flow along the part's length.
      const flowGeom = buildFlowSlices(THREE, parts, flowSlices, slotStart, slotEnd);
      if (flowGeom) {
        root.add(new THREE.LineSegments(flowGeom, sharedMaterial));
      }
    } else {
      // Edge rendering: feature lines via EdgesGeometry, with parts
      // sorted left→right or bottom→top by world-space center.
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const p of parts) {
        if (p.center.x < minX) minX = p.center.x;
        if (p.center.x > maxX) maxX = p.center.x;
        if (p.center.y < minY) minY = p.center.y;
        if (p.center.y > maxY) maxY = p.center.y;
      }
      const sortAxis = (maxX - minX) >= (maxY - minY) ? 'x' : 'y';
      parts.sort((a, b) => a.center[sortAxis] - b.center[sortAxis]);

      const M = parts.length;
      for (let p = 0; p < M; p++) {
        const partT = M === 1 ? 0.5 : p / (M - 1);
        const reveal = slotStart + (slotEnd - slotStart) * partT;

        const edgesGeom = new THREE.EdgesGeometry(parts[p].geometry, edgeAngle);
        const count = edgesGeom.attributes.position.count;
        const aReveal = new Float32Array(count).fill(reveal);
        edgesGeom.setAttribute('aReveal', new THREE.BufferAttribute(aReveal, 1));

        const lines = new THREE.LineSegments(edgesGeom, sharedMaterial);
        root.add(lines);
      }
    }

    // Free the source mesh geometries — line buffers are independent.
    for (const p of parts) p.geometry.dispose();

    // Yield to the event loop after each model so the browser can
    // render the partial assembly + respond to input. Without this
    // the page freezes during slice / EdgesGeometry computation.
    await new Promise((r) => setTimeout(r, 0));
  }

  status.remove();

  // Frame the assembled root at the camera.
  const overall = new THREE.Box3().setFromObject(root);
  const size = overall.getSize(new THREE.Vector3());
  const center = overall.getCenter(new THREE.Vector3());
  root.position.sub(center);
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  const fov = (camera.fov * Math.PI) / 180;
  const dist = (maxDim / (2 * Math.tan(fov / 2))) * 1.6;
  camera.position.set(dist * 0.6, maxDim * 0.15, dist);
  camera.near = dist / 1000;
  camera.far = dist * 100;
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();

  const controls = new OrbitControlsClass(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;
  controls.enablePan = false;
  controls.minDistance = dist * 0.2;
  controls.maxDistance = dist * 4;
  controls.enabled = !block.scrubScroll;

  if (block.hint) {
    const tag = document.createElement('div');
    tag.className = 'hint';
    tag.textContent = block.hint;
    container.appendChild(tag);
  }

  // Resize
  const ro = new ResizeObserver(() => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  });
  ro.observe(container);

  // Visibility for render gating + auto-play trigger
  let inView = false;
  let playStart = null;
  const visIO = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        inView = e.isIntersecting;
        if (e.intersectionRatio >= 0.3 && playStart === null && !block.scrubScroll) {
          playStart = performance.now();
        }
      }
    },
    { threshold: [0, 0.3, 0.6, 1] }
  );
  visIO.observe(container);

  const duration = (block.duration ?? 6) * 1000;
  const loop = block.loop ?? true;
  const loopHold = (block.loopHold ?? 1.5) * 1000;
  const loopFade = (block.loopFade ?? 0.4) * 1000;
  let raf = 0;

  function tick() {
    raf = requestAnimationFrame(tick);
    if (!inView) return;
    let progress;
    if (block.scrubScroll && typeof getScrollProgress === 'function') {
      progress = getScrollProgress(container);
    } else if (playStart != null) {
      const elapsed = performance.now() - playStart;
      if (loop) {
        const cycle = duration + loopHold + loopFade;
        const t = elapsed % cycle;
        if (t < duration) {
          progress = t / duration;
        } else if (t < duration + loopHold) {
          progress = 1;
        } else {
          // Brief fade-out before next cycle so the loop isn't jarring.
          const f = (t - duration - loopHold) / loopFade;
          progress = Math.max(0, 1 - f);
        }
      } else {
        progress = Math.min(1, elapsed / duration);
      }
    } else {
      progress = 0;
    }
    sharedMaterial.uniforms.uProgress.value = progress;
    controls.update();
    renderer.render(scene, camera);
  }
  tick();

  return {
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      visIO.disconnect();
      sharedMaterial.dispose();
      root.traverse((o) => o.geometry?.dispose?.());
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}

/**
 * Load a pre-baked .bin file produced by scripts/bake-lines.mjs and
 * render it as one indexed LineSegments draw call. The vertex shader
 * decodes int16-quantized positions back to world space using bbox
 * uniforms baked into the file header.
 */
async function mountBakedLines(container, block, url, getScrollProgress) {
  const THREE = THREE_NS;

  // Preview mode: render the model fully assembled and spinning, no
  // scroll handling — used by the index gallery cards. Declared up
  // front because several setup branches downstream branch on it.
  const previewMode = !!block.previewMode;
  const useLock = !!block.scrubScroll && !previewMode;

  const status = document.createElement('div');
  status.className = 'hint';
  status.textContent = 'Loading';
  container.appendChild(status);

  let buffer;
  try {
    const r = await fetch(url, { cache: 'no-store' });
    if (!r.ok) throw new Error(`${url}: ${r.status}`);
    buffer = await r.arrayBuffer();
  } catch (err) {
    console.error('[baked] fetch failed', err);
    status.textContent = 'Failed to load';
    return null;
  }

  // Optional sidecar with per-entry names. Used by the debug overlay
  // that labels the currently-revealing part during scroll. Missing
  // sidecar is non-fatal — the overlay just stays empty.
  let modelNames = null;
  try {
    const namesUrl = url.replace(/\.bin$/i, '.names.json');
    const nr = await fetch(namesUrl, { cache: 'no-store' });
    if (nr.ok) modelNames = await nr.json();
  } catch {}

  const view = new DataView(buffer);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== 'RCLN') {
    console.error('[baked] bad magic', magic);
    status.remove();
    return null;
  }
  const version = view.getUint32(4, true);
  if (version !== 9) {
    console.error('[baked] unsupported version (rebake required)', version);
    status.remove();
    return null;
  }
  const lineVerts = view.getUint32(8, true);
  const lineIdxCount = view.getUint32(12, true);
  const pointVerts = view.getUint32(16, true);
  const modelCount = view.getUint32(20, true);
  const bboxMin = new THREE.Vector3(
    view.getFloat32(24, true),
    view.getFloat32(28, true),
    view.getFloat32(32, true)
  );
  const bboxMax = new THREE.Vector3(
    view.getFloat32(36, true),
    view.getFloat32(40, true),
    view.getFloat32(44, true)
  );
  const maxFlyMag = view.getFloat32(48, true);
  const maxLiftMag = view.getFloat32(52, true);
  const lowerStart = view.getFloat32(56, true);
  const lowerEnd = view.getFloat32(60, true);

  // Per-model bbox + slot range + lift offset (44 bytes/entry)
  const models = new Array(modelCount);
  let off = 64;
  for (let i = 0; i < modelCount; i++) {
    const min = new THREE.Vector3(
      view.getFloat32(off + 0, true),
      view.getFloat32(off + 4, true),
      view.getFloat32(off + 8, true)
    );
    const max = new THREE.Vector3(
      view.getFloat32(off + 12, true),
      view.getFloat32(off + 16, true),
      view.getFloat32(off + 20, true)
    );
    const center = min.clone().add(max).multiplyScalar(0.5);
    const size = max.clone().sub(min);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const slotStart = view.getFloat32(off + 24, true);
    const slotEnd = view.getFloat32(off + 28, true);
    const lift = new THREE.Vector3(
      view.getFloat32(off + 32, true),
      view.getFloat32(off + 36, true),
      view.getFloat32(off + 40, true)
    );
    models[i] = { min, max, center, size, maxDim, slotStart, slotEnd, lift };
    off += 44;
  }

  // ── LINES section ────────────────────────────────────────────────
  let linePos = null, lineRev = null, lineFly = null, lineOp = null, lineLift = null, lineIndices = null;
  if (lineVerts > 0) {
    linePos = new Int16Array(buffer, off, lineVerts * 3);
    off += lineVerts * 3 * 2;
    off = (off + 3) & ~3;
    lineRev = new Float32Array(buffer, off, lineVerts);
    off += lineVerts * 4;
    lineFly = new Int8Array(buffer, off, lineVerts * 3);
    off += lineVerts * 3;
    off = (off + 3) & ~3;
    lineOp = new Uint8Array(buffer, off, lineVerts);
    off += lineVerts;
    off = (off + 3) & ~3;
    lineLift = new Int8Array(buffer, off, lineVerts * 3);
    off += lineVerts * 3;
    off = (off + 3) & ~3;
    if (lineIdxCount > 0) {
      lineIndices = new Uint32Array(buffer, off, lineIdxCount);
      off += lineIdxCount * 4;
    }
  }

  // ── POINTS section ──────────────────────────────────────────────
  let pointPos = null, pointRev = null, pointFly = null, pointOp = null, pointLift = null;
  if (pointVerts > 0) {
    pointPos = new Int16Array(buffer, off, pointVerts * 3);
    off += pointVerts * 3 * 2;
    off = (off + 3) & ~3;
    pointRev = new Float32Array(buffer, off, pointVerts);
    off += pointVerts * 4;
    pointFly = new Int8Array(buffer, off, pointVerts * 3);
    off += pointVerts * 3;
    off = (off + 3) & ~3;
    pointOp = new Uint8Array(buffer, off, pointVerts);
    off += pointVerts;
    off = (off + 3) & ~3;
    pointLift = new Int8Array(buffer, off, pointVerts * 3);
    off += pointVerts * 3;
    off = (off + 3) & ~3;
  }

  const center = bboxMin.clone().add(bboxMax).multiplyScalar(0.5);
  const halfRange = bboxMax.clone().sub(bboxMin).multiplyScalar(0.5);

  function makeGeometry(pos, rev, fly, op, lift, idx) {
    const g = new THREE.BufferGeometry();
    const posAttr = new THREE.Int16BufferAttribute(pos, 3);
    posAttr.normalized = true;
    g.setAttribute('position', posAttr);
    g.setAttribute('aReveal', new THREE.Float32BufferAttribute(rev, 1));
    const flyAttr = new THREE.Int8BufferAttribute(fly, 3);
    flyAttr.normalized = true;
    g.setAttribute('aFlyDir', flyAttr);
    const opAttr = new THREE.Uint8BufferAttribute(op, 1);
    opAttr.normalized = true;
    g.setAttribute('aOpacity', opAttr);
    const liftAttr = new THREE.Int8BufferAttribute(lift, 3);
    liftAttr.normalized = true;
    g.setAttribute('aLift', liftAttr);
    if (idx) g.setIndex(new THREE.Uint32BufferAttribute(idx, 1));
    return g;
  }
  const lineGeom = lineVerts > 0 ? makeGeometry(linePos, lineRev, lineFly, lineOp, lineLift, lineIndices) : null;
  const pointGeom = pointVerts > 0 ? makeGeometry(pointPos, pointRev, pointFly, pointOp, pointLift, null) : null;

  const lineColor = new THREE.Color(block.lineColor || '#ffffff');
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uProgress: { value: 0 },
      uColor: { value: lineColor },
      uOpacity: { value: block.lineOpacity ?? 0.7 },
      uSoftness: { value: block.lineSoftness ?? 0.04 },
      uOffset: { value: center },
      uScale: { value: halfRange },
      // Effective fly distance = uFlyDistance × maxFlyMag, so a stored
      // unit vector flies the full distance and shorter vectors (those
      // from non-multiplied folders) fly proportionally less.
      uFlyDistance: { value: (block.flyInDistance ?? 1.0) * (maxFlyMag || 1.0) },
      uFlyWindow: { value: block.flyInWindow ?? 0.05 },
      // Physical point "diameter" in world units. Multiplied by the
      // viewport-height factor and divided by view-space depth so
      // points scale naturally as the camera zooms in/out.
      uPointWorldSize: { value: block.pointWorldSize ?? 0.012 },
      uPointScale: { value: 600 },
      // Lift offset world distance — multiplied by per-vertex aLift
      // (which is in [-1,1] normalized to maxLiftMag).
      uLiftDistance: { value: maxLiftMag || 1.0 },
      uLowerStart: { value: lowerStart },
      uLowerEnd: { value: lowerEnd },
    },
    vertexShader: /* glsl */ `
      attribute float aReveal;
      attribute vec3 aFlyDir;
      attribute float aOpacity;
      attribute vec3 aLift;
      uniform vec3 uOffset;
      uniform vec3 uScale;
      uniform float uProgress;
      uniform float uFlyDistance;
      uniform float uFlyWindow;
      uniform float uPointWorldSize;
      uniform float uPointScale;
      uniform float uLiftDistance;
      uniform float uLowerStart;
      uniform float uLowerEnd;
      varying float vReveal;
      varying float vOpacity;
      void main() {
        vReveal = aReveal;
        vOpacity = aOpacity;
        vec3 worldPos = uOffset + position * uScale;
        float flyT = 1.0 - smoothstep(aReveal - uFlyWindow, aReveal, uProgress);
        worldPos += aFlyDir * (uFlyDistance * flyT);
        // Lift: vertices in lifted folder assemble at +aLift × distance,
        // then are lowered to rest position over the [lowerStart, lowerEnd]
        // window. Other vertices have aLift = 0 so this is a no-op.
        float lowerT = uLowerEnd > uLowerStart
          ? smoothstep(uLowerStart, uLowerEnd, uProgress)
          : 1.0;
        worldPos += aLift * (uLiftDistance * (1.0 - lowerT));
        vec4 mvPos = modelViewMatrix * vec4(worldPos, 1.0);
        gl_Position = projectionMatrix * mvPos;
        gl_PointSize = max(1.0, uPointWorldSize * uPointScale / -mvPos.z);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform float uProgress;
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uSoftness;
      varying float vReveal;
      varying float vOpacity;
      void main() {
        // Shifted smoothstep: vReveal is the "fully arrived" moment.
        // Items are fully visible at uProgress = vReveal, fading in
        // over the preceding uSoftness window. Pre-revealed items
        // (reveal <= 0) are visible immediately at uProgress = 0.
        float vis = smoothstep(vReveal - uSoftness, vReveal, uProgress);
        float a = vis * uOpacity * vOpacity;
        if (a < 0.001) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(block.background || '#000000');

  // Two draw calls sharing one material — LineSegments for edges/
  // contours, Points for the surface-sampled forks/sticks.
  if (lineGeom) scene.add(new THREE.LineSegments(lineGeom, material));
  if (pointGeom) scene.add(new THREE.Points(pointGeom, material));

  const camera = new THREE.PerspectiveCamera(32, 1, 0.001, 100000);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.appendChild(renderer.domElement);

  if (block.hint) {
    const tag = document.createElement('div');
    tag.className = 'hint';
    tag.textContent = block.hint;
    container.appendChild(tag);
  }

  // Debug overlay: labels the currently-revealing entry during scroll.
  // Hidden when the names sidecar is missing.
  const partLabel = document.createElement('div');
  partLabel.style.cssText = [
    'position:absolute',
    'left:12px',
    'bottom:12px',
    'padding:4px 8px',
    'font:11px/1.3 ui-monospace,SFMono-Regular,Menlo,monospace',
    'color:#fff',
    'background:rgba(0,0,0,0.55)',
    'border:1px solid rgba(255,255,255,0.15)',
    'border-radius:3px',
    'pointer-events:none',
    'z-index:5',
    'max-width:80%',
    'opacity:0',
    'transition:opacity 120ms',
  ].join(';');
  // Part-label overlay disabled — was useful while debugging the
  // assembly order, distracting in the final piece.
  // if (modelNames && !previewMode) container.appendChild(partLabel);
  let lastLabelIdx = -1;

  status.remove();

  const ro = new ResizeObserver(() => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    // Aspect change → re-fit the overall framing so portrait
    // viewports don't clip the assembly horizontally.
    overallDist = overallDistFor(camera.aspect);
    // Point-size attenuation factor: half the drawing-buffer height
    // is the standard scale Three.js uses for sizeAttenuation.
    const dbSize = renderer.getDrawingBufferSize(new THREE.Vector2());
    material.uniforms.uPointScale.value = dbSize.y * 0.5;
  });
  ro.observe(container);

  let inView = false;
  const visIO = new IntersectionObserver(
    (entries) => {
      for (const e of entries) inView = e.isIntersecting;
    },
    { threshold: [0, 0.3, 0.6, 1] }
  );
  visIO.observe(container);

  // ── Scroll-lock controller ─────────────────────────────────────────
  // While the section is centered in the viewport, capture scroll
  // input and drive `virtualProgress` instead of the page. Releases
  // back to native scroll when progress hits 0 or 1 in the matching
  // direction.
  let virtualProgress = 0;
  let lockState = 'idle'; // 'idle' | 'locked' | 'released'
  // On phones, give a much higher sensitivity so the whole assembly
  // can be triggered in a couple of swipes instead of a minute of
  // dragging. Falls back to the desktop value when no mobile-specific
  // setting is provided.
  const isNarrow = typeof window !== 'undefined'
    && window.matchMedia?.('(max-width: 900px)')?.matches;
  const wheelSensitivity =
    (isNarrow && block.wheelSensitivityMobile != null
      ? block.wheelSensitivityMobile
      : block.wheelSensitivity) ?? 0.0008;
  const touchSensitivity =
    (isNarrow && block.touchSensitivityMobile != null
      ? block.touchSensitivityMobile
      : block.touchSensitivity) ?? 0.003;
  let touchPrevY = 0;
  let onWheel = null;
  let onTouchStart = null;
  let onTouchMove = null;
  let onKeyDown = null;
  let lockObs = null;

  // Boundary pressure: at progress 0 or 1, continuing input in the
  // boundary direction accumulates pressure; once it crosses the
  // release threshold the lock disengages and the page resumes
  // scrolling. Reversing direction (or any mid-range delta) clears
  // pressure, so the user can scroll back through the animation
  // even after reaching either end.
  let boundaryPressure = 0;
  let pressureTimer = null;
  const releasePressure = block.releasePressure ?? 0.4;

  function clearPressure() {
    boundaryPressure = 0;
    if (pressureTimer) {
      clearTimeout(pressureTimer);
      pressureTimer = null;
    }
  }
  function bumpPressure(amount) {
    boundaryPressure += amount;
    if (pressureTimer) clearTimeout(pressureTimer);
    // Decay pressure if input pauses — sustained push releases, a tap
    // or a pause-then-reverse does not.
    pressureTimer = setTimeout(() => {
      boundaryPressure = 0;
      pressureTimer = null;
    }, 500);
  }

  function applyDelta(delta) {
    const atTop = virtualProgress >= 0.9999;
    const atBottom = virtualProgress <= 0.0001;

    // Mid-range, OR reversing away from a boundary → advance and
    // clear any accumulated pressure.
    if ((!atTop && !atBottom) || (atTop && delta < 0) || (atBottom && delta > 0)) {
      clearPressure();
      virtualProgress = Math.max(0, Math.min(1, virtualProgress + delta));
      return;
    }

    // At a boundary, pushing into it: accumulate pressure.
    const intoDir = atTop ? delta : -delta;
    bumpPressure(intoDir);
    if (boundaryPressure >= releasePressure) {
      virtualProgress = atTop ? 1 : 0;
      clearPressure();
      releaseLock();
    }
  }

  function engageLock() {
    if (lockState !== 'idle') return;
    lockState = 'locked';
    const lenis = getLenis();
    if (lenis) lenis.stop();
    document.documentElement.classList.add('is-scroll-locked');

    onWheel = (e) => {
      e.preventDefault();
      applyDelta(e.deltaY * wheelSensitivity);
    };
    onTouchStart = (e) => {
      touchPrevY = e.touches[0].clientY;
    };
    onTouchMove = (e) => {
      e.preventDefault();
      const y = e.touches[0].clientY;
      const dy = touchPrevY - y;
      touchPrevY = y;
      applyDelta(dy * touchSensitivity);
    };
    onKeyDown = (e) => {
      let d = 0;
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') d = 0.04;
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') d = -0.04;
      else if (e.key === 'Home') { virtualProgress = 0; e.preventDefault(); return; }
      else if (e.key === 'End')  { virtualProgress = 1; e.preventDefault(); return; }
      else return;
      e.preventDefault();
      applyDelta(d);
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('keydown', onKeyDown);
  }

  function releaseLock() {
    if (lockState !== 'locked') return;
    lockState = 'released';
    const lenis = getLenis();
    if (lenis) lenis.start();
    document.documentElement.classList.remove('is-scroll-locked');
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('touchstart', onTouchStart);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('keydown', onKeyDown);
  }

  if (useLock) {
    // Engage threshold has to be reachable on mobile. iOS Safari's
    // dynamic browser chrome (address bar + bottom toolbar) eats
    // ~15% of `100vh`, so a 100vh section caps at ~0.85 intersection
    // ratio when the bars are visible. 0.95 would never engage on
    // phones and the user just scrolls past the model. Release stays
    // well below engage to avoid flutter at the boundary.
    lockObs = new IntersectionObserver(
      (entries) => {
        const r = entries[0].intersectionRatio;
        if (lockState === 'idle' && r >= 0.85) {
          engageLock();
        } else if (lockState === 'released' && r < 0.6) {
          lockState = 'idle';
        }
      },
      { threshold: [0, 0.3, 0.5, 0.6, 0.85, 1] }
    );
    lockObs.observe(container);
  }

  const padding = block.cameraPadding ?? 1.6;
  const rotationSpeed = block.rotationSpeed ?? 0.12; // rad/s, gentle
  const smoothingHz = block.cameraSmoothing ?? 4; // higher = snappier
  const fovRad = (camera.fov * Math.PI) / 180;
  const overallCenter = bboxMin.clone().add(bboxMax).multiplyScalar(0.5);
  const sizeX = bboxMax.x - bboxMin.x;
  const sizeY = bboxMax.y - bboxMin.y;
  const sizeZ = bboxMax.z - bboxMin.z;
  const overallMaxDim = Math.max(sizeX, sizeY, sizeZ);
  // Axis-aligned horizontal extent. We fit when the model is upright
  // (yaw 0 / 90 / 180 / 270°), not the 45° diagonal worst-case —
  // using the diagonal here was pulling the camera way back on
  // portrait phones and leaving the model tiny in the frame.
  const maxXZ = Math.max(sizeX, sizeZ);
  // Aspect-aware distance fit. Vertical FOV alone is fine on
  // landscape viewports, but on portrait (phones) the horizontal
  // FOV gets very narrow — the same model would clip on the sides.
  // We pick the distance that fits BOTH axes for the current aspect.
  function overallDistFor(aspect) {
    const tanHalf = Math.tan(fovRad / 2);
    const distVert = sizeY / (2 * tanHalf);
    const distHoriz = maxXZ / (2 * tanHalf * Math.max(aspect, 0.001));
    return Math.max(distVert, distHoriz) * padding;
  }
  let overallDist = overallDistFor(1);

  // Smoothed state
  const smoothedTarget = overallCenter.clone();
  let smoothedDist = overallDist;
  let smoothedLiftFactor = 0;
  let baseYaw = Math.PI * 0.25;
  const tmpVec = new THREE.Vector3();

  const clock = new THREE.Clock();
  let raf = 0;

  function fitDistanceFor(maxDim) {
    return (maxDim / (2 * Math.tan(fovRad / 2))) * padding;
  }

  function tick() {
    raf = requestAnimationFrame(tick);
    if (!inView) return;

    const dt = Math.min(0.05, clock.getDelta());
    const elapsed = clock.elapsedTime;

    // Reveal progress: scroll-locked virtual progress, scroll-track
    // progress (legacy), or auto-play.
    let progress;
    if (previewMode) {
      // Always fully assembled — gallery cards just spin the final
      // state, no reveal animation.
      progress = 1;
    } else if (useLock) {
      progress = virtualProgress;
    } else if (block.scrubScroll && typeof getScrollProgress === 'function') {
      progress = getScrollProgress(container);
    } else {
      progress = Math.min(1, elapsed / (block.duration ?? 8));
    }
    material.uniforms.uProgress.value = progress;

    // Preview mode OR `cameraFollow: false`: frame the WHOLE
    // assembly, not the per-entry bbox. Per-entry follow logic
    // below would otherwise zoom into individual parts (small parts
    // can get cropped or take the camera far off-center). Reveal
    // animation still plays — only the camera framing is fixed.
    //
    // Lifted entries (e.g. the glulam node, which assembles above
    // and descends) are the exception: we DO follow their vertical
    // offset, so the lifted piece stays in frame and the descent
    // reads as a deliberate move.
    if (previewMode || block.cameraFollow === false) {
      const N0 = models.length;
      let m0 = null;
      let liftFactor = 0;
      if (N0 > 0 && !previewMode) {
        let i = 0;
        for (; i < N0 - 1; i++) if (progress < models[i].slotEnd) break;
        m0 = models[i];
        const m0Lifted = m0.lift.x !== 0 || m0.lift.y !== 0 || m0.lift.z !== 0;
        if (m0Lifted) {
          const lowerSpan = lowerEnd - lowerStart;
          const lowerT = lowerSpan > 0
            ? Math.max(0, Math.min(1, (progress - lowerStart) / lowerSpan))
            : 0;
          liftFactor = 1 - lowerT;
        }
        // Update the debug overlay with the active entry name (same
        // behaviour as the per-entry path).
        if (modelNames && i !== lastLabelIdx) {
          lastLabelIdx = i;
          const name = modelNames[i] || '';
          partLabel.textContent = `[${i + 1}/${N0}] ${name}`;
          partLabel.style.opacity = name ? '0.85' : '0';
        }
      }

      tmpVec.copy(overallCenter);
      // Preview cards use a tighter vertical-only fit (same as the
      // GLB viewer's fitToView) so the model fills the card the
      // same way the flower does next to it. The default
      // overallDist adds the XZ diagonal as a worst-case horizontal
      // extent — fine for the assembly page where the model is
      // viewed wide, but it wastes a lot of the card area.
      let targetDist = previewMode
        ? (overallMaxDim / (2 * Math.tan(fovRad / 2))) * (block.cameraPadding ?? 1.4)
        : overallDist;
      if (liftFactor > 0 && m0) {
        tmpVec.x += m0.lift.x * liftFactor;
        tmpVec.y += m0.lift.y * liftFactor;
        tmpVec.z += m0.lift.z * liftFactor;
        // Pull back so both the lifted node and the assembly below
        // stay in view. cameraLiftZoom (default 1.0) controls how
        // much to widen the framing during the hold.
        const liftMag = Math.hypot(m0.lift.x, m0.lift.y, m0.lift.z);
        const zoomFactor = block.cameraLiftZoom ?? 1.0;
        const zoomOut = 1 + liftFactor * (liftMag / (overallMaxDim || 1)) * zoomFactor;
        targetDist *= zoomOut;
      }

      const k = smoothingHz >= 100 ? 1 : 1 - Math.exp(-dt * smoothingHz);
      smoothedTarget.lerp(tmpVec, k);
      smoothedDist += (targetDist - smoothedDist) * k;
      smoothedLiftFactor += (liftFactor - smoothedLiftFactor) * k;

      const yaw = baseYaw + elapsed * rotationSpeed;
      const liftPitch = block.cameraLiftPitch ?? 0;
      const pitch = -0.18 - smoothedLiftFactor * liftPitch;
      const r = smoothedDist;
      const cy = Math.cos(yaw), sy = Math.sin(yaw);
      const cp = Math.cos(pitch), sp = Math.sin(pitch);
      camera.position.set(
        smoothedTarget.x + r * cp * sy,
        smoothedTarget.y - r * sp,
        smoothedTarget.z + r * cp * cy
      );
      camera.lookAt(smoothedTarget);
      renderer.render(scene, camera);
      return;
    }

    // Camera follows each model in full 3D. Within a slot we hold
    // on the current model for the first `holdFrac` of the slot,
    // then smoothstep over the remaining portion to land on the
    // next model. Result: long stable views with smooth transitions
    // between, instead of a constantly-drifting camera.
    const N = models.length;
    if (N > 0) {
      // Find current model by slot range.
      let i = 0;
      for (; i < N - 1; i++) {
        if (progress < models[i].slotEnd) break;
      }
      const m0 = models[i];
      const m1 = models[Math.min(N - 1, i + 1)];
      const range = m0.slotEnd - m0.slotStart || 1;
      const local = Math.max(0, Math.min(1, (progress - m0.slotStart) / range));

      // Update the debug overlay with the active entry's name. Index
      // changes are debounced via lastLabelIdx so we don't touch the
      // DOM every frame.
      if (modelNames && i !== lastLabelIdx) {
        lastLabelIdx = i;
        const name = modelNames[i] || '';
        partLabel.textContent = `[${i + 1}/${N}] ${name}`;
        partLabel.style.opacity = name ? '0.85' : '0';
      }

      const m0Lifted = m0.lift.x !== 0 || m0.lift.y !== 0 || m0.lift.z !== 0;

      // Two transition modes:
      //  - Normal: hold for first holdFrac of slot, then smoothstep.
      //  - Lifted model: hold for the WHOLE slot until the lower
      //    window fires, then transition + descent happen together.
      //    This keeps the camera locked on the assembled node above
      //    the structure for the duration of its slot.
      const holdFrac = block.cameraHoldFraction ?? 0.5;
      let t;
      let liftFactor; // 1 = fully lifted, 0 = at rest
      if (m0Lifted) {
        const lowerSpan = lowerEnd - lowerStart;
        const lowerT = lowerSpan > 0
          ? Math.max(0, Math.min(1, (progress - lowerStart) / lowerSpan))
          : 0;
        t = lowerT;
        liftFactor = 1 - lowerT;
      } else {
        if (local < holdFrac) {
          t = 0;
        } else {
          const u = (local - holdFrac) / (1 - holdFrac);
          t = u * u * (3 - 2 * u);
        }
        liftFactor = 0;
      }

      tmpVec.lerpVectors(m0.center, m1.center, t);
      const focusMaxDim = m0.maxDim * (1 - t) + m1.maxDim * t;

      // Pull target toward overall center for small parts so they
      // don't crop out the rest of the assembly. Larger parts get a
      // tight, dedicated framing.
      const sizeRatio = Math.min(1, focusMaxDim / (overallMaxDim || 1));
      const contextBlend = (block.cameraContextBlend ?? 0.5) * (1 - sizeRatio);
      tmpVec.lerp(overallCenter, contextBlend);

      // Apply m0's lift to the target AFTER the context blend so the
      // lift isn't diluted. The camera target tracks the lifted node
      // by (overall_center + lift), keeping the assembled piece in
      // frame even when contextBlend is high. liftFactor fades to 0
      // over the lower window in sync with the geometry shader.
      if (liftFactor > 0) {
        tmpVec.x += m0.lift.x * liftFactor;
        tmpVec.y += m0.lift.y * liftFactor;
        tmpVec.z += m0.lift.z * liftFactor;
      }

      // Lock the camera target's horizontal position to the overall
      // assembly center — only height (Y) tracks the active part.
      // Stops the camera from sliding left/right as different parts
      // are visited.
      if (block.cameraLockHorizontal !== false) {
        tmpVec.x = overallCenter.x;
        tmpVec.z = overallCenter.z;
      }

      const minDistRatio = block.cameraMinDistRatio ?? 0.3;
      const partFit = fitDistanceFor(focusMaxDim) * (block.cameraPadding ?? 1.7);
      let targetDist = Math.max(overallDist * minDistRatio, partFit);
      // While we're held on the lifted model, expand the framing by
      // the lift's vertical reach so both the assembled node and the
      // assembly below stay in view. cameraLiftZoom controls how
      // aggressively we zoom out (lower = closer); cameraLiftPitch
      // adds extra downward tilt so the camera looks more from above.
      if (liftFactor > 0) {
        const liftMag = Math.hypot(m0.lift.x, m0.lift.y, m0.lift.z);
        const zoomFactor = block.cameraLiftZoom ?? 1.0;
        const zoomOut = 1 + liftFactor * (liftMag / (overallMaxDim || 1)) * zoomFactor;
        targetDist *= zoomOut;
      }

      const k = smoothingHz >= 100 ? 1 : 1 - Math.exp(-dt * smoothingHz);
      smoothedTarget.lerp(tmpVec, k);
      smoothedDist += (targetDist - smoothedDist) * k;
      smoothedLiftFactor += (liftFactor - smoothedLiftFactor) * k;
    }

    // Continuous gentle rotation around the target, plus a fixed
    // slight tilt down. Extra downward tilt while a lifted model is
    // assembled — looks "from above" at the suspended node.
    const yaw = baseYaw + elapsed * rotationSpeed;
    const liftPitch = block.cameraLiftPitch ?? 0;
    const pitch = -0.18 - smoothedLiftFactor * liftPitch;
    const r = smoothedDist;
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    const cp = Math.cos(pitch);
    const sp = Math.sin(pitch);
    camera.position.set(
      smoothedTarget.x + r * cp * sy,
      smoothedTarget.y - r * sp,
      smoothedTarget.z + r * cp * cy
    );
    camera.lookAt(smoothedTarget);

    renderer.render(scene, camera);
  }
  tick();

  return {
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      visIO.disconnect();
      lockObs?.disconnect();
      releaseLock();
      material.dispose();
      lineGeom?.dispose();
      pointGeom?.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };
}

/**
 * Slice the assembled `parts` perpendicular to their combined
 * bounding box's longest axis at `slicesCount` evenly-spaced planes.
 * Each plane intersects every triangle in every part and produces
 * one line segment per crossed triangle. Per-vertex `aReveal` values
 * stagger across slices, so lines reveal as a flow along the axis.
 */
function buildFlowSlices(THREE, parts, slicesCount, slotStart, slotEnd) {
  const combined = new THREE.Box3();
  for (const p of parts) combined.union(p.geometry.boundingBox);
  const size = combined.getSize(new THREE.Vector3());

  // Pick the longest axis of the combined bbox.
  let axisIdx = 0;
  if (size.y > size.x && size.y >= size.z) axisIdx = 1;
  else if (size.z > size.x && size.z > size.y) axisIdx = 2;
  const minA = combined.min.getComponent(axisIdx);
  const maxA = combined.max.getComponent(axisIdx);
  if (maxA === minA) return null;

  const range = maxA - minA;
  const sliceStep = range / slicesCount;
  const positions = [];
  const reveals = [];
  const v0 = new THREE.Vector3();
  const v1 = new THREE.Vector3();
  const v2 = new THREE.Vector3();

  // Triangle-driven: each triangle decides which slice planes it
  // crosses (its bbox along the axis intersects which planes), then
  // computes intersections only for those. Replaces an O(slices ×
  // triangles) loop with one closer to O(triangles × avgPlanesHit).
  for (const part of parts) {
    const pos = part.geometry.attributes.position;
    const idx = part.geometry.index;
    const triCount = idx ? (idx.count / 3) | 0 : (pos.count / 3) | 0;

    for (let i = 0; i < triCount; i++) {
      const i3 = i * 3;
      const i0 = idx ? idx.getX(i3) : i3;
      const i1 = idx ? idx.getX(i3 + 1) : i3 + 1;
      const i2 = idx ? idx.getX(i3 + 2) : i3 + 2;
      v0.fromBufferAttribute(pos, i0);
      v1.fromBufferAttribute(pos, i1);
      v2.fromBufferAttribute(pos, i2);

      const a0 = v0.getComponent(axisIdx);
      const a1 = v1.getComponent(axisIdx);
      const a2 = v2.getComponent(axisIdx);
      const tMin = Math.min(a0, a1, a2);
      const tMax = Math.max(a0, a1, a2);

      // Slice indices whose plane lies within [tMin, tMax]. Plane s
      // sits at (s + 0.5) * sliceStep + minA.
      const sFirst = Math.max(0, Math.ceil((tMin - minA) / sliceStep - 0.5));
      const sLast = Math.min(slicesCount - 1, Math.floor((tMax - minA) / sliceStep - 0.5));
      if (sFirst > sLast) continue;

      for (let s = sFirst; s <= sLast; s++) {
        const planeValue = minA + (s + 0.5) * sliceStep;
        const reveal = slotStart + ((slotEnd - slotStart) * (s + 0.5)) / slicesCount;
        const d0 = a0 - planeValue;
        const d1 = a1 - planeValue;
        const d2 = a2 - planeValue;

        let foundCount = 0;
        const px = [0, 0], py = [0, 0], pz = [0, 0];

        if ((d0 >= 0) !== (d1 >= 0)) {
          const u = d0 / (d0 - d1);
          px[foundCount] = v0.x + (v1.x - v0.x) * u;
          py[foundCount] = v0.y + (v1.y - v0.y) * u;
          pz[foundCount] = v0.z + (v1.z - v0.z) * u;
          foundCount++;
        }
        if ((d1 >= 0) !== (d2 >= 0)) {
          const u = d1 / (d1 - d2);
          px[foundCount] = v1.x + (v2.x - v1.x) * u;
          py[foundCount] = v1.y + (v2.y - v1.y) * u;
          pz[foundCount] = v1.z + (v2.z - v1.z) * u;
          foundCount++;
        }
        if (foundCount < 2 && ((d2 >= 0) !== (d0 >= 0))) {
          const u = d2 / (d2 - d0);
          px[foundCount] = v2.x + (v0.x - v2.x) * u;
          py[foundCount] = v2.y + (v0.y - v2.y) * u;
          pz[foundCount] = v2.z + (v0.z - v2.z) * u;
          foundCount++;
        }
        if (foundCount === 2) {
          positions.push(px[0], py[0], pz[0], px[1], py[1], pz[1]);
          reveals.push(reveal, reveal);
        }
      }
    }
  }

  if (positions.length === 0) return null;

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute('aReveal', new THREE.Float32BufferAttribute(reveals, 1));
  return geom;
}

function makeLineMaterial(THREE, color, opacity = 0.7) {
  return new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uProgress: { value: 0 },
      uColor: { value: color },
      uOpacity: { value: opacity },
      uSoftness: { value: 0.04 },
    },
    vertexShader: /* glsl */ `
      attribute float aReveal;
      varying float vReveal;
      void main() {
        vReveal = aReveal;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;
      uniform float uProgress;
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uSoftness;
      varying float vReveal;
      void main() {
        float vis = smoothstep(vReveal - uSoftness, vReveal + uSoftness, uProgress);
        float a = vis * uOpacity;
        if (a < 0.001) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });
}

// Computes the bbox of `obj` (must enclose every visible part —
// world-space `setFromObject` walks all descendants), recenters
// the model on a rotation pivot (a designated part's center, or the
// combined bbox center if none is given), and sizes the camera fit
// to enclose the swept volume the model occupies as it autorotates
// around Y. Without the sweep correction, asymmetric parts would
// pop out of the frustum at certain rotation angles. Returns the
// effective `size` so resize handlers can re-fit cheaply.
function fitToView(obj, camera, padding = 1.4, controls = null, pivotObj = null, elevation = 0.15) {
  obj.updateMatrixWorld(true);
  const totalBox = new THREE_NS.Box3().setFromObject(obj);
  let pivot;
  if (pivotObj) {
    const pBox = new THREE_NS.Box3().setFromObject(pivotObj);
    pivot = pBox.getCenter(new THREE_NS.Vector3());
  } else {
    pivot = totalBox.getCenter(new THREE_NS.Vector3());
  }
  obj.position.sub(pivot);
  // Symmetric extents from the rotation pivot. (Swept-cylinder fit
  // was tried and made cube-ish models look ~30% too small. Mid-
  // rotation clipping is instead prevented by setting
  // frustumCulled = false on the meshes at load time.)
  const hx = Math.max(Math.abs(totalBox.min.x - pivot.x), Math.abs(totalBox.max.x - pivot.x));
  const hy = Math.max(Math.abs(totalBox.min.y - pivot.y), Math.abs(totalBox.max.y - pivot.y));
  const hz = Math.max(Math.abs(totalBox.min.z - pivot.z), Math.abs(totalBox.max.z - pivot.z));
  const size = new THREE_NS.Vector3(2 * hx, 2 * hy, 2 * hz);
  applyCameraFit(camera, size, padding, controls, elevation);
  return size;
}

// Apply the camera fit using a cached bbox `size`. Considers both
// vertical AND horizontal FOV so the model fits whichever axis is
// the tighter constraint for the current aspect — a wide model in a
// narrow container fits horizontally, a tall model fits vertically.
// Adds `size.z / 2` so the camera doesn't sit inside the model.
// `elevation` is a multiplier on size.y for the camera's vertical
// offset (0 = level with pivot; 0.5 = at the top of the model;
// negative = below). Default 0.15 is a gentle overhead tilt.
function applyCameraFit(camera, size, padding, controls, elevation = 0.15) {
  const fovV = (camera.fov * Math.PI) / 180;
  const aspect = camera.aspect || 1;
  const fovH = 2 * Math.atan(Math.tan(fovV / 2) * aspect);
  const distV = (size.y / 2) / Math.tan(fovV / 2);
  const distH = (size.x / 2) / Math.tan(fovH / 2);
  const dist = Math.max(distV, distH) * padding + size.z / 2;
  camera.position.set(0, size.y * elevation, dist);
  // Tighter near plane so surfaces close to the camera (e.g. the
  // outer hull of an enclosing "air" mesh) don't pop in/out as the
  // model rotates.
  camera.near = Math.max(dist / 1000, 0.0005);
  camera.far = dist * 50 + size.length() * 4;
  camera.updateProjectionMatrix();
  // OrbitControls default minDistance/maxDistance is fixed in world
  // units (1.5–8). For models far smaller or larger than the default
  // 1 m scale, those bounds would clamp the camera away from the
  // model. Rescale the range around the just-computed fit distance.
  if (controls) {
    controls.target.set(0, 0, 0);
    controls.minDistance = dist * 0.3;
    controls.maxDistance = dist * 4;
    controls.update();
  }
}

function buildTorusKnot(THREE) {
  const geo = new THREE.TorusKnotGeometry(0.7, 0.22, 220, 32);
  const mat = new THREE.MeshStandardMaterial({
    color: 0xf3f3f3,
    metalness: 0.9,
    roughness: 0.2,
  });
  return new THREE.Mesh(geo, mat);
}

function buildSphere(THREE) {
  const geo = new THREE.IcosahedronGeometry(1, 4);
  const mat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    metalness: 0.1,
    roughness: 0.4,
    flatShading: true,
  });
  return new THREE.Mesh(geo, mat);
}

function buildFallback(THREE) {
  const geo = new THREE.BoxGeometry(1, 1, 1);
  const mat = new THREE.MeshStandardMaterial({ color: 0x999999 });
  return new THREE.Mesh(geo, mat);
}
